import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Languages, Menu, Search, Zap, FileText, LogOut, SlidersHorizontal, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWallet } from '../hooks/useWallet';
import { TenantSwitcher } from './TenantSwitcher';
import httpClient from '../services/httpClient';

const LANG_OPTIONS = [
  { code: 'en', label: 'EN' },
  { code: 'el', label: 'EL' },
  { code: 'es', label: 'ES' },
  { code: 'hu', label: 'HU' },
  { code: 'lv', label: 'LV' },
  { code: 'bg', label: 'BG' },
];

const ICON_MAP = { contract: FileText, service: Zap };

const NOTIF_COLORS = {
  contract: '#3D6B38',
  service: '#1565C0',
};

const relativeTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return `${Math.floor(hrs / 24)} d ago`;
};

const IconAction = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="btn-header-icon focus-ring outline-none"
  >
    {children}
  </button>
);

/**
 * Header / TopBar - translucent glass bar with page title, search and icon actions.
 */
export const Header = ({ title, subtitle, onMenuClick, isHome = false }) => {
  const { i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { addressDisplay, isConnected, isConnecting, connectWallet } = useWallet();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);
  const langRef = useRef(null);
  const profileRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const userName = user?.full_name || user?.email?.split('@')?.[0] || 'User';

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await httpClient.get('/notifications');
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, []);

  const toggleNotifications = () => {
    setNotifOpen((open) => {
      const next = !open;
      if (next) {
        setLangOpen(false);
        setProfileOpen(false);
      }
      return next;
    });
  };

  const toggleLanguage = () => {
    setLangOpen((open) => {
      const next = !open;
      if (next) {
        setNotifOpen(false);
        setProfileOpen(false);
      }
      return next;
    });
  };

  const toggleProfile = () => {
    setProfileOpen((open) => {
      const next = !open;
      if (next) {
        setNotifOpen(false);
        setLangOpen(false);
      }
      return next;
    });
  };

  /* Close dropdowns on outside click */
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    window.addEventListener('notifications:refresh', fetchNotifications);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications:refresh', fetchNotifications);
    };
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await httpClient.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error('Failed to mark all read', err);
    }
  };

  const markRead = async (id) => {
    try {
      await httpClient.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error('Failed to mark read', err);
    }
  };

  const handleWalletClick = async () => {
    if (isConnected) {
      navigate('/data-exchange');
      return;
    }
    try {
      await connectWallet();
    } catch {
      // Wallet errors are surfaced by the wallet flow when available.
    }
  };

  return (
    <header
      className="flex-shrink-0 px-5 sm:px-6"
      style={{
        position: 'relative',
        zIndex: 60,
        height: '66px',
        background: 'var(--canvas)',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <div className="page-column flex h-full items-center gap-4">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={onMenuClick}
          className="lg:hidden text-lb-muted hover:text-lb-ink transition-colors p-1.5 rounded-lg focus-ring outline-none"
          aria-label="Open navigation"
        >
          <Menu size={20} />
        </button>

        {/* Page title */}
        <div className="flex-1 min-w-0 flex items-center gap-1.5 text-[12px] text-lb-muted">
          {isHome ? (
            <span className="font-semibold text-lb-ink">Home</span>
          ) : (
            <>
              <span>Home</span>
              <span className="opacity-50">/</span>
              <span className="font-semibold text-lb-ink truncate">{title}</span>
            </>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
        <div className="hidden xl:block">
          <TenantSwitcher />
        </div>

        {/* Search pill */}
        <label
          className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-pill text-[13px] text-lb-muted cursor-text transition-all
                     focus-within:ring-1 focus-within:ring-lb-green-accent/40"
          style={{ background: 'var(--surface-2)', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', minWidth: '280px', height: '38px' }}
        >
          <Search size={13} strokeWidth={2.2} className="text-lb-ghost flex-shrink-0" aria-hidden="true" />
          <input
            type="search"
            placeholder="Search buildings, assets, service:"
            aria-label="Search buildings, assets, service"
            className="bg-transparent border-none outline-none w-full text-lb-body placeholder:text-lb-ghost text-[13px]"
          />
        </label>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <IconAction
            label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
            onClick={toggleNotifications}
          >
            <Bell size={15} strokeWidth={1.9} />
          </IconAction>
          {unreadCount > 0 && (
            <span
              className="absolute top-1.5 right-1.5 w-[7px] h-[7px] rounded-full bg-lb-critical"
              aria-hidden="true"
            />
          )}

          {/* Notifications dropdown */}
          {notifOpen && (
            <div
              className="absolute right-0 top-full mt-2 z-50 rounded-2xl overflow-hidden"
              style={{
                width: '360px',
                maxHeight: '440px',
                background: 'rgba(255,255,255,0.97)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.14)',
                backdropFilter: 'blur(20px)',
                display: 'flex',
                flexDirection: 'column',
              }}
              role="dialog"
              aria-label="Notifications"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <span className="text-[14px] font-bold text-lb-ink">Notifications</span>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-[11px] font-semibold text-lb-green-primary hover:underline outline-none focus-ring rounded"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {/* List */}
              <div className="overflow-y-auto flex-1" style={{ maxHeight: '360px' }}>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-[13px] text-lb-ghost">
                    No notifications yet.
                  </div>
                ) : (
                  notifications.map((n) => {
                    const Icon = ICON_MAP[n.type] || Bell;
                    const accent = NOTIF_COLORS[n.type] || '#3D6B38';
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => markRead(n.id)}
                        className="w-full text-left flex items-start gap-3 px-4 py-3 transition-colors hover:bg-black/[0.03] outline-none focus-ring border-0 rounded-none appearance-none"
                        style={{
                          border: 'none',
                          borderBottom: '1px solid rgba(0,0,0,0.04)',
                          background: n.read ? 'transparent' : 'rgba(61,107,56,0.04)',
                        }}
                      >
                        <div className="mt-1 flex-shrink-0">
                          <Icon size={15} strokeWidth={1.9} style={{ color: accent }} aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[12.5px] font-semibold text-lb-ink truncate">
                              {n.title}
                            </span>
                            {!n.read && (
                              <span className="w-[6px] h-[6px] rounded-full bg-lb-green-primary flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-[11.5px] text-lb-muted leading-snug mt-0.5 line-clamp-2">
                            {n.message}
                          </p>
                          <span className="text-[10px] text-lb-ghost mt-1 block">{relativeTime(n.created_at)}</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Language picker */}
        <div className="relative" ref={langRef}>
          <IconAction label="Change language" onClick={toggleLanguage}>
            <Languages size={15} strokeWidth={1.9} />
          </IconAction>
          {langOpen && (
            <div
              className="absolute right-0 top-full mt-2 z-50 rounded-card-sm overflow-hidden py-1 min-w-[80px]"
              style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', backdropFilter: 'blur(16px)' }}
              role="menu"
              aria-label="Language options"
            >
              {LANG_OPTIONS.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  role="menuitem"
                  onClick={() => { i18n.changeLanguage(opt.code); setLangOpen(false); }}
                  className={`w-full px-4 py-2 text-left text-[13px] font-medium transition-colors border-0 rounded-none appearance-none
                    ${i18n.language === opt.code
                      ? 'text-lb-green-primary bg-lb-green-mist/30'
                      : 'text-lb-body hover:bg-black/[0.04]'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User avatar + profile dropdown */}
        <div className="relative" ref={profileRef}>
          <button
            type="button"
            onClick={toggleProfile}
            className="hidden md:inline-flex btn-header-icon focus-ring outline-none"
            title={userName}
            aria-label={`Profile menu for ${userName}`}
          >
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={userName}
                className="w-7 h-7 rounded-full object-cover"
              />
            ) : (
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #3D6B38, #6BAF65)' }}
              >
                {userName.slice(0, 1).toUpperCase()}
              </span>
            )}
          </button>

          {/* Profile dropdown menu */}
          {profileOpen && (
            <div
              className="absolute right-0 top-full mt-2 z-50 rounded-2xl overflow-hidden py-1"
              style={{
                width: '220px',
                background: 'rgba(255,255,255,0.97)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.14)',
                backdropFilter: 'blur(20px)',
              }}
              role="menu"
              aria-label="Profile options"
            >
              {/* User info */}
              <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <p className="text-[13px] font-bold text-lb-ink truncate">{userName}</p>
                <p className="text-[11px] text-lb-muted truncate">{user?.email || ''}</p>
              </div>

              <button
                type="button"
                role="menuitem"
                onClick={() => { setProfileOpen(false); navigate('/settings'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-medium text-lb-body hover:bg-black/[0.04] transition-colors border-0 rounded-none appearance-none"
              >
                <SlidersHorizontal size={14} strokeWidth={2} className="text-lb-muted" />
                Settings
              </button>
              <div className="h-px bg-black/[0.06] my-1" />
              <button
                type="button"
                role="menuitem"
                onClick={async () => { setProfileOpen(false); await signOut(); navigate('/login'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors border-0 rounded-none appearance-none"
              >
                <LogOut size={14} strokeWidth={2} />
                Sign Out
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleWalletClick}
          disabled={isConnecting}
          className="btn-header-cta focus-ring outline-none"
        >
          <Wallet size={14} strokeWidth={2} aria-hidden="true" />
          {isConnected ? (addressDisplay || 'Wallet') : isConnecting ? 'Connecting...' : 'Connect wallet'}
        </button>
        </div>
      </div>
    </header>
  );
};
